import { NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  OnChanges,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, fromEvent, merge } from 'rxjs';

import { getNamespaceId } from '@blaze-runtime/engine-domain';
import { markEventAsHandled } from '@blaze-runtime/engine-util';
import { getBackground, makeContainerVisualEffectStyles } from '@blaze-shared/elements-domain';
import {
  findParentHTMLElement,
  resizeRxUtils,
  setCssPropertyValue,
} from '@blaze-shared/shared-util';

import { SlotNodeDirective } from '../../canvas-elements/rendering/slot-node.directive';
import { CloseAffordanceRegistry } from '../../services/close-affordance-registry.service';
import { DialogOverlayService } from '../../services/dialog/dialog-overlay.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { ResizeDrawerComponent } from './resize-drawer/resize-drawer.component';

@UntilDestroy()
@Component({
  selector: 'be-drawer',
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'entering' },
  imports: [ResizeDrawerComponent, MatIcon, NgStyle, SlotNodeDirective, NgTemplateOutlet],
})
export class DrawerComponent
  extends CanvasElementBaseComponent<'drawer'>
  implements OnChanges, AfterViewInit
{
  private readonly closeAffordance = inject(CloseAffordanceRegistry);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogOverlay = inject(DialogOverlayService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  private readonly closeButton = viewChild<ElementRef<HTMLElement>>('closeButton');

  @HostBinding('style') drawerStyle: Record<string, string> = {};

  private rootElement?: HTMLDivElement;
  private scrollTopOnOpen = 0;
  private currentOverflow = 0;

  contentStyle!: Record<string, string>;

  constructor() {
    super();
    // Unlike the dialog, the drawer renders its close button unconditionally over the content.
    const closeButton = computed(() => this.closeButton()?.nativeElement ?? null);
    this.destroyRef.onDestroy(
      this.closeAffordance.register(this.elementRef.nativeElement, closeButton),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['slots'] || changes['props']) {
      this.updateStyles();
    }
  }

  ngAfterViewInit(): void {
    this.rootElement = document.querySelector(
      '.blaze-target.blaze-element.type-root',
    ) as HTMLDivElement;
    this.scrollTopOnOpen = window.scrollY;

    merge(
      resizeRxUtils.fromResizeObserver(document.body),
      resizeRxUtils.fromResizeObserver(this.elementRef.nativeElement),
    )
      .pipe(debounceTime(20), untilDestroyed(this))
      .subscribe(() => {
        this.updateDrawerOverlap();
        this.updateDrawerOverflow();
      });

    fromEvent(document, 'scroll')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateDrawerOverflow();
      });

    const overlay = this.getOverlayContainer();
    if (!overlay) {
      throw new Error('overlay not found');
    }

    overlay.addEventListener(
      'animationend',
      () => {
        this.elementRef.nativeElement.classList.remove('entering');
      },
      { once: true },
    );
  }

  closeDrawer(event: MouseEvent): void {
    // ignore non-left mouse button clicks
    if (event.button !== 0) {
      return;
    }
    markEventAsHandled(event);
    const overlay = this.getOverlayContainer();
    if (!overlay) {
      throw new Error('overlay not found');
    }

    overlay.addEventListener(
      'animationend',
      () => {
        if (this.elementId) {
          this.dialogOverlay.closeDialog(getNamespaceId(this.elementId));
        }
      },
      { once: true },
    );
    overlay.classList.add('exiting');
  }

  private getOverlayContainer(): HTMLElement | undefined {
    return findParentHTMLElement(this.elementRef.nativeElement, (e) => e.tagName === 'BE-OVERLAY')
      ?.firstElementChild as HTMLElement | undefined;
  }

  private updateStyles(): void {
    if (this.props) {
      const { container } = this.props.style ?? {};
      const drawerStyle: Record<string, string> = {
        ...(container?.visualEffect
          ? makeContainerVisualEffectStyles(container.visualEffect)
          : undefined),
        padding: container?.offset.padding ?? '0px',
      };
      if (container?.outline.color1) {
        drawerStyle['border-color'] = container.outline.color1;
      }
      if (container?.fill) {
        drawerStyle['background'] = getBackground(container?.fill);
      }

      this.drawerStyle = drawerStyle;
    }
    if (this.slots) {
      const contentSlotStyle = (this.slots.slotNodesData['content']?.props as any)?.style;
      this.contentStyle = { padding: contentSlotStyle?.padding };
    }
  }

  private updateDrawerOverlap(): void {
    let spaceLeft = this.rootElement?.offsetLeft ?? Number.POSITIVE_INFINITY;
    if (spaceLeft === 1) {
      spaceLeft = 0;
    }
    const drawerWidth = this.elementRef.nativeElement.clientWidth;
    const width = spaceLeft > drawerWidth ? 0 : Math.min(spaceLeft, drawerWidth - spaceLeft);
    setCssPropertyValue('--drawer-overlap', `-${width}px`);
  }

  private updateDrawerOverflow(): void {
    const drawerHeight = this.elementRef.nativeElement.clientHeight;
    const { clientHeight } = document.body;
    let drawerOverflow = 0;
    if (drawerHeight > clientHeight) {
      drawerOverflow = Math.max(
        Math.min(this.scrollTopOnOpen - window.scrollY, 0), // prevent going downwards
        clientHeight - drawerHeight, // prevent going offscreen upwards
      );
    }

    if (this.currentOverflow === drawerOverflow) {
      return;
    }
    this.currentOverflow = drawerOverflow;
    setCssPropertyValue('--drawer-overflow', `${drawerOverflow}px`);
  }
}
